import { useEffect, useLayoutEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { isMac } from '@/utils/platform';
import { HELP_MENU_ORDER, HelpMenuSlot } from './HelpMenu';
import { AgentContainer } from './AgentContainer';

const PANEL_WIDTH = '400px';

/**
 * Mounted once at the authenticated-layout level so the AI sidebar item is
 * always visible. Each page can opt into AI assistance by calling
 * `useAgentModule()`, which registers a `moduleConfig`. While no module is
 * registered the trigger renders as a disabled sidebar item with a tooltip
 * explaining where the assistant is available.
 */
export function FloatingAgentPanel() {
  const gt = useGT();
  const { enabled } = useFeatureFlag('chat-agent-enabled', false);
  const { isOpen, toggle, close } = useAgentPanel();
  const { moduleConfig } = useAgentContainer();

  const hasModule = moduleConfig !== null;
  const panelActive = enabled && hasModule && isOpen;
  const width = panelActive ? PANEL_WIDTH : '0px';

  // Sync the layout CSS variable that `SidebarInset` reads for its right
  // padding. Use `useLayoutEffect` so the DOM update happens synchronously
  // after commit (before paint), avoiding a one-frame flash where the inset
  // padding lags the panel open/close state. Reset to '0px' on unmount so a
  // remount on a fresh page never inherits a stale width.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--agent-panel-width', width);
    return () => {
      root.style.setProperty('--agent-panel-width', '0px');
    };
  }, [width]);

  // If the user opens the panel on a page that registers an agent module and
  // then navigates somewhere that doesn't, drop the open state so we don't
  // leave a phantom panel slot stuck in the layout.
  useEffect(() => {
    if (!hasModule && isOpen) close();
  }, [hasModule, isOpen, close]);

  // Cmd/Ctrl+Shift+A toggles the AI panel. Only active when the feature is
  // enabled AND the current page has registered an agent module.
  useHotkeys(
    'mod+shift+a',
    () => {
      if (enabled && hasModule) toggle();
    },
    { preventDefault: true, enabled: enabled && hasModule },
    [enabled, hasModule, toggle],
  );

  if (!enabled) return null;

  const shortcutLabel = isMac() ? '⌘⇧A' : 'Ctrl+Shift+A';

  const enabledTooltip = gt('Open AI assistant ({shortcut})', {
    context:
      'tooltip on the collapsed sidebar AI assistant menu item, includes keyboard shortcut',
    shortcut: shortcutLabel,
  });
  const disabledTooltip = gt('AI assistant — available on workflow pages', {
    context:
      'tooltip explaining that the AI assistant is only enabled on certain pages',
  });

  return (
    <>
      {/* Sidebar trigger — always rendered (when the feature is enabled) so
          users can discover the AI assistant from anywhere. Disabled when no
          page has registered an agent module. Stays visible while the panel
          is open so clicking it acts as a close affordance. */}
      <HelpMenuSlot order={HELP_MENU_ORDER.ai}>
        <SidebarMenuButton
          onClick={hasModule ? () => toggle() : undefined}
          disabled={!hasModule}
          isActive={panelActive}
          className="cursor-pointer"
          aria-label={gt('Open AI assistant', {
            context: 'sidebar help menu button aria label for AI assistant',
          })}
          tooltip={hasModule ? enabledTooltip : disabledTooltip}
        >
          <SparkleIcon weight="fill" className="text-purple-600" />
          {/* Trailing label/shortcut block — hidden in icon-mode so the
              SidebarMenuButton can center the icon inside its 32×32 shell. */}
          <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <T context="sidebar help menu item label for opening the AI assistant">
              AI assistant
            </T>
            <kbd className="rounded border border-sidebar-border/60 bg-sidebar-accent/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {shortcutLabel}
            </kbd>
          </span>
        </SidebarMenuButton>
      </HelpMenuSlot>

      {/* Fixed side panel — only mounted when a module is registered, so we
          never show an empty container. */}
      {panelActive && (
        <div className="bg-background fixed right-0 top-0 z-50 h-screen w-[400px] border-l shadow-xl">
          <AgentContainer className="flex h-full w-full flex-col overflow-hidden pb-6" />
        </div>
      )}
    </>
  );
}
